/**
 * Sticky Add to Cart Component
 * Dynamically generates options from main form and keeps them in perfect sync
 */
class StickyCart {
  constructor() {
    this.stickyElement = document.getElementById("sticky-add-to-cart");
    this.mainAddToCartButton = null;
    this.observer = null;
    this.stickyButton = null;
    this.optionsContainer = null;

    if (this.stickyElement) {
      this.init();
    }
  }

  init() {
    this.mainAddToCartButton = document.querySelector("button.add-to-cart");
    this.stickyButton = document.getElementById("sticky-add-to-cart-button");
    this.optionsContainer = document.getElementById("sticky-atc-options");

    this.generateStickyOptions();
    this.setupIntersectionObserver();
    this.bindEvents();
  }

  generateStickyOptions() {
    if (!this.optionsContainer) return;

    // Find all product option containers generically
    const optionContainers = document.querySelectorAll(".product-option, .colors, .sizes, .variants");

    optionContainers.forEach((container, index) => {
      const buttons = container.querySelectorAll("button");
      if (buttons.length > 0) {
        this.generateGenericDropdown(container, buttons, index);
      }
    });

    console.log("Generated sticky options from main form (generic approach)");
  }

  generateGenericDropdown(container, buttons, index) {
    // Determine option label from the container
    let optionLabel = this.getOptionLabel(container);

    // Create unique ID for this dropdown
    const selectId = `sticky-option-${index}`;

    const dropdown = this.createOptionDropdown(optionLabel, selectId);

    buttons.forEach((button) => {
      // Get value - try different attributes that might exist
      const value =
        button.getAttribute("data") || button.getAttribute("data-id") || button.dataset.data || button.dataset.id;

      // Get display text
      const text = button.getAttribute("data-text") || button.dataset.text || button.textContent.trim();

      const isSelected = button.classList.contains("active");

      if (value && text) {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = text;

        // Store additional data that might be useful
        if (button.getAttribute("data-id")) {
          option.setAttribute("data-variant-id", button.getAttribute("data-id"));
        }

        if (isSelected) option.selected = true;

        dropdown.select.appendChild(option);
      }
    });

    // Only add if we got some options
    if (dropdown.select.children.length > 0) {
      this.optionsContainer.insertBefore(dropdown.container, this.optionsContainer.firstChild);
    }
  }

  getOptionLabel(container) {
    // Try different ways to get the option label

    // Method 1: Look for label in the same container
    const label = container.querySelector(".option-label");
    if (label) {
      return label.textContent.replace(":", "").trim();
    }

    // Method 2: Look for label in previous sibling (common pattern)
    const prevSibling = container.previousElementSibling;
    if (prevSibling && prevSibling.classList.contains("option-label")) {
      // Only get the text content of the option-label, ignoring any children elements
      return prevSibling.childNodes[0]?.textContent?.replace(":", "").trim() || "";
    }

    // Method 3: Look for .label-flex within container
    const labelFlex = container.querySelector(".label-flex label");
    if (labelFlex) {
      return labelFlex.textContent.replace(":", "").trim();
    }

    // Method 4: Use container class name as fallback
    if (container.classList.contains("colors")) return "Color";
    if (container.classList.contains("sizes")) return "Size";
    if (container.classList.contains("variants")) return "Option";

    // Method 5: Generic fallback
    return "Option";
  }

  createOptionDropdown(labelText, selectId) {
    const container = document.createElement("div");
    container.className = "sticky-option";

    const label = document.createElement("label");
    label.setAttribute("for", selectId);
    label.textContent = labelText + ":";

    const select = document.createElement("select");
    select.id = selectId;
    select.className = "sticky-product-option";

    container.appendChild(label);
    container.appendChild(select);

    return { container, select };
  }

  setupIntersectionObserver() {
    if (!this.mainAddToCartButton) return;

    // Use scroll event for more precise control over when to show sticky cart
    const checkButtonPosition = () => {
      const buttonRect = this.mainAddToCartButton.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const topQuarter = viewportHeight * 0.25;

      // Show sticky cart when button is in top 25% of screen or above
      if (buttonRect.top <= topQuarter) {
        this.showStickyCart();
      } else {
        this.hideStickyCart();
      }
    };

    // Initial check
    checkButtonPosition();

    // Listen for scroll events
    window.addEventListener('scroll', checkButtonPosition);

    // Store the function reference for cleanup
    this.scrollHandler = checkButtonPosition;
  }

  showStickyCart() {
    this.stickyElement.classList.add("is-visible");
  }

  hideStickyCart() {
    this.stickyElement.classList.remove("is-visible");
  }

  bindEvents() {
    // Prevent clicks on sticky cart from bubbling up
    if (this.stickyElement) {
      this.stickyElement.addEventListener("click", (e) => {
        e.stopPropagation();
      });
    }

    // Bind main form events to update sticky cart
    this.bindMainFormEvents();

    // Bind sticky cart events to update main form
    this.bindStickyFormEvents();
  }

  bindMainFormEvents() {
    // GENERIC approach - find any buttons in option containers
    console.log("🔄 Main form sync with generic approach");

    // Find all option buttons generically
    const optionButtons = document.querySelectorAll(
      ".product-option button, .colors button, .sizes button, .variants button",
    );

    optionButtons.forEach((button) => {
      button.addEventListener("click", () => {
        setTimeout(() => this.syncMainToSticky(), 100);
      });
    });

    console.log(`Found ${optionButtons.length} option buttons to monitor`);

    // Listen for main quantity changes
    const mainQuantity = document.getElementById("quantity");
    if (mainQuantity) {
      mainQuantity.addEventListener("change", () => {
        this.syncMainQuantityToSticky();
      });
    }
  }

  bindStickyFormEvents() {
    // Handle sticky option changes
    const stickyOptions = document.querySelectorAll(".sticky-product-option");
    stickyOptions.forEach((select) => {
      select.addEventListener("change", (e) => {
        e.stopPropagation();
        this.syncStickyToMain(e.target);
      });

      select.addEventListener("click", (e) => e.stopPropagation());
    });

    // Handle sticky quantity changes
    const stickyQuantity = document.getElementById("sticky-quantity");
    if (stickyQuantity) {
      stickyQuantity.addEventListener("change", (e) => {
        e.stopPropagation();
        this.syncStickyQuantityToMain();
      });

      stickyQuantity.addEventListener("click", (e) => e.stopPropagation());
    }

    // Handle sticky add to cart
    if (this.stickyButton) {
      this.stickyButton.addEventListener("click", (e) => {
        e.stopPropagation();
        if (this.mainAddToCartButton && !this.mainAddToCartButton.disabled) {
          this.mainAddToCartButton.click();
        }
      });
    }

    // Prevent other clicks from bubbling
    const stickyContainer = document.querySelector(".sticky-atc-container");
    if (stickyContainer) {
      stickyContainer.addEventListener("click", (e) => {
        const allowedElements = ["SELECT", "INPUT", "BUTTON"];
        if (!allowedElements.includes(e.target.tagName)) {
          e.preventDefault();
          e.stopPropagation();
        }
      });
    }
  }

  syncStickyToMain(changedSelect) {
    const selectedValue = changedSelect.value;
    console.log(`Syncing sticky dropdown change: ${selectedValue}`);

    // Try to find a matching button with this value in any option container
    const targetButton = document.querySelector(
      `.product-option button[data="${selectedValue}"], .product-option button[data-id="${selectedValue}"], ` +
        `.colors button[data="${selectedValue}"], .colors button[data-id="${selectedValue}"], ` +
        `.sizes button[data="${selectedValue}"], .sizes button[data-id="${selectedValue}"], ` +
        `.variants button[data="${selectedValue}"], .variants button[data-id="${selectedValue}"]`,
    );

    if (targetButton && !targetButton.classList.contains("active")) {
      console.log("Found matching button, clicking it");
      targetButton.click();
    } else {
      // Fallback: update the hidden product select field directly
      console.log("No matching button found, updating hidden field");
      const productSelect = document.querySelector(".product-select");
      if (productSelect) {
        productSelect.value = selectedValue;
        console.log("Updated product select to:", selectedValue);
      }
    }
  }

  syncMainQuantityToSticky() {
    const mainQuantity = document.getElementById("quantity");
    const stickyQuantity = document.getElementById("sticky-quantity");
    if (mainQuantity && stickyQuantity) {
      stickyQuantity.value = mainQuantity.value;
    }
  }

  syncStickyQuantityToMain() {
    const stickyQuantity = document.getElementById("sticky-quantity");
    const mainQuantity = document.getElementById("quantity");
    if (stickyQuantity && mainQuantity) {
      mainQuantity.value = stickyQuantity.value;
    }
  }

  updateStickyPrice() {
    console.log("🔍 updateStickyPrice called");
    const mainPrice = document.querySelector(".price");
    const stickyPrice = document.getElementById("sticky-product-price");

    console.log("Main price element:", mainPrice);
    console.log("Sticky price element:", stickyPrice);

    if (mainPrice && stickyPrice) {
      console.log("Before update - sticky cart length:", this.stickyElement.innerHTML.length);
      console.log("Main price content:", mainPrice.innerHTML);

      // Only update the text content, not innerHTML to be safer
      stickyPrice.textContent = mainPrice.textContent;

      console.log("After update - sticky cart length:", this.stickyElement.innerHTML.length);

      // Check if something went wrong
      if (this.stickyElement.innerHTML.length < 500) {
        console.error("🚨 CONTENT WAS REPLACED! Current content:", this.stickyElement.innerHTML);
      }
    }
  }

  updateStickyButtonState() {
    const mainButton = this.mainAddToCartButton;

    if (mainButton && this.stickyButton) {
      this.stickyButton.textContent = mainButton.textContent;
      this.stickyButton.disabled = mainButton.disabled;

      if (mainButton.classList.contains("success")) {
        this.stickyButton.classList.add("success");
      } else {
        this.stickyButton.classList.remove("success");
      }
    }
  }

  syncMainToSticky() {
    console.log("🛡️ syncMainToSticky called");

    // Check if sticky cart still exists and has content
    if (!this.stickyElement || this.stickyElement.innerHTML.length < 500) {
      console.error("🚨 Sticky cart content missing, skipping sync");
      return;
    }

    // Only update dropdown values - never touch content
    try {
      // Find all active buttons in any option container
      const activeButtons = document.querySelectorAll(
        ".product-option button.active, .colors button.active, .sizes button.active, .variants button.active",
      );

      // Find all sticky dropdowns
      const stickySelects = document.querySelectorAll(".sticky-product-option");

      // For each active button, try to find matching sticky dropdown
      activeButtons.forEach((button) => {
        const value =
          button.getAttribute("data") || button.getAttribute("data-id") || button.dataset.data || button.dataset.id;

        if (value) {
          // Find sticky dropdown that has this value as an option
          stickySelects.forEach((select) => {
            const matchingOption = select.querySelector(`option[value="${value}"]`);
            if (matchingOption && select.value !== value) {
              select.value = value;
              console.log(`Updated ${select.id} dropdown to:`, value);
            }
          });
        }
      });

      // Update price text only (safest way)
      const mainPrice = document.querySelector(".price");
      const stickyPrice = document.getElementById("sticky-product-price");
      if (mainPrice && stickyPrice && mainPrice.textContent !== stickyPrice.textContent) {
        stickyPrice.textContent = mainPrice.textContent;
        console.log("Updated price to:", mainPrice.textContent);
      }

      console.log("✅ Generic safe sync completed without issues");
    } catch (error) {
      console.error("❌ Error in generic safe sync:", error);
    }
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
  }
}

// Initialize when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  window.stickyCart = new StickyCart();
});

// Cleanup on page unload
window.addEventListener("beforeunload", () => {
  if (window.stickyCart) {
    window.stickyCart.destroy();
  }
});
